import React, { useState } from "react"
import { Checkbox, Input, Modal } from "antd"
import type { NetOption } from "../Inspector/AddNetModal.component"

export interface BannerNetModalProps {
  open: boolean
  title: string
  /**
   * The answers the net can hold, in the order to list them: each by its
   * label (`value`, what the net stores) and the name it is shown under.
   */
  options: NetOption[]
  onCancel: () => void
  /** Adds the net; returns the sentence to show under the name when it is refused, or null. */
  onConfirm: (name: string, members: string[]) => string | null
  getContainer?: () => HTMLElement
}

/**
 * Asks for a net's name and members. A refusal from `onConfirm` stays under
 * the name until the name or the members change; the dialog forgets what was
 * typed when it is closed.
 */
const BannerNetModal: React.FC<BannerNetModalProps> = ({
  open,
  title,
  options,
  onCancel,
  onConfirm,
  getContainer,
}) => {
  const [name, setName] = useState("")
  const [members, setMembers] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const reset = (): void => {
    setName("")
    setMembers([])
    setError(null)
  }
  const confirm = (): void => {
    const refusal = onConfirm(name, members)
    if (refusal) setError(refusal)
    else reset()
  }

  return (
    <Modal
      open={open}
      title={title}
      okText="Add"
      onOk={confirm}
      onCancel={() => {
        reset()
        onCancel()
      }}
      getContainer={getContainer}
      destroyOnHidden
    >
      <div className="ts-net-modal">
        <Input
          aria-label="Net name"
          placeholder="Net name"
          value={name}
          status={error ? "error" : undefined}
          onChange={(e) => {
            setName(e.target.value)
            setError(null)
          }}
          onPressEnter={confirm}
        />
        {error && (
          <p className="ts-net-modal__error" role="alert">
            {error}
          </p>
        )}
        <Checkbox.Group
          className="ts-net-modal__members"
          aria-label="Members"
          value={members}
          options={options}
          onChange={(chosen) => {
            setMembers(chosen as string[])
            setError(null)
          }}
        />
      </div>
    </Modal>
  )
}

export default BannerNetModal
